import React from 'react';
import { Portal } from 'react-portal';
import { Helmet } from 'react-helmet';
import { eventOrder } from '~modules/browser';
import t from '~t';
import { cn } from './lib/utils';
import { Button } from './button';
import { Icon } from './icon';
import { Header, HeaderTitle, HeaderSpace, HeaderLastAction } from './header';

/* Port of `~co/overlay/modal` — centered dialog in a portal, topmost-only
   Escape + `important` click-swallowing via eventOrder. Styles live
   unlayered in global.css (.ui-modal*). */

const ModalContext = React.createContext({});

export class Modal extends React.Component {
  static defaultProps = {
    closable: true,
    important: false,
    hidden: false,
    onClose: undefined,
  };

  body = React.createRef();

  componentDidMount() {
    if (!this.props.hidden) this.addListeners();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.hidden !== this.props.hidden) {
      if (this.props.hidden) this.removeListeners();
      else this.addListeners();
    }
  }

  componentWillUnmount() {
    this.removeListeners();
  }

  addListeners() {
    eventOrder.add(this);
    window.addEventListener('keydown', this.onWindowKeyDown);
    window.addEventListener('mousedown', this.onWindowMouseDown, true);
  }

  removeListeners() {
    eventOrder.delete(this);
    window.removeEventListener('keydown', this.onWindowKeyDown);
    window.removeEventListener('mousedown', this.onWindowMouseDown, true);
  }

  onWindowKeyDown = (e) => {
    switch (e.key) {
      case 'Escape':
        if (!eventOrder.isLast(this)) return;

        e.preventDefault();
        e.stopPropagation();
        this.props.onClose();
        break;
    }
  };

  onWindowMouseDown = (e) => {
    //prevent clicking outside when important true
    if (this.props.important && !this.body.current?.contains(e.target)) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  render() {
    const {
      as = 'div',
      children,
      onClose,
      closable,
      important,
      hidden,
      stretch,
      className,
      ...etc
    } = this.props;
    const Component = as;

    return (
      <Portal>
        <ModalContext.Provider value={{ onClose, closable }}>
          {!hidden && (
            <Helmet defer={false}>
              <html data-modal-showing data-modal-stretch={stretch} />
            </Helmet>
          )}

          <div
            className={cn(
              'ui-modal',
              important && 'ui-modal-important',
              hidden && 'ui-modal-hidden',
            )}
          >
            <Component
              className={cn('ui-modal-wrap', className)}
              data-stretch={stretch}
              {...etc}
            >
              <div ref={this.body} className='ui-modal-body' role='dialog'>
                {children}
              </div>
            </Component>
          </div>
        </ModalContext.Provider>
      </Portal>
    );
  }
}

export default Modal;

export class ModalHeader extends React.Component {
  static contextType = ModalContext;

  render() {
    const { title, children, ...etc } = this.props;

    return (
      <Header {...etc}>
        {this.context.closable ? (
          <Button
            variant='ghost'
            size='icon'
            className='ui-modal-back'
            title={t.s('close')}
            onClick={this.context.onClose}
          >
            <Icon name='back' />
          </Button>
        ) : null}

        {title && <HeaderTitle>{title}</HeaderTitle>}

        <HeaderSpace />

        {children}

        {this.context.closable ? (
          <HeaderLastAction className='ui-modal-close'>
            <Button
              variant='ghost'
              size='icon'
              title={t.s('close')}
              onClick={this.context.onClose}
            >
              <Icon name='close' />
            </Button>
          </HeaderLastAction>
        ) : null}
      </Header>
    );
  }
}

export const ModalContent = React.forwardRef(function ModalContent(
  { as = 'div', children, className, ...props },
  ref,
) {
  const Component = as;
  return (
    <Component
      {...props}
      ref={ref}
      className={cn('ui-modal-content', className)}
    >
      {children}
    </Component>
  );
});
